import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  Heart,
  ArrowRight,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Code,
} from 'lucide-react';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';
import BackToTop from '../../components/ui/BackToTop';
import {
  getProjectBySlug,
  getStatusLabel,
  getStatusColor,
  showcaseProjects,
} from '../../data/showcaseProjects';

const ProjectDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const project = slug ? getProjectBySlug(slug) : undefined;

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const otherProjects = showcaseProjects.filter((p) => p.slug !== project.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <main id="main-content" className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/projects"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500 no-underline hover:no-underline mb-8"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            All Projects
          </Link>

          {/* Header */}
          <div className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <h1 className="text-3xl sm:text-4xl font-bold text-secondary-900">
                {project.title}
              </h1>
              <span
                className={`inline-flex items-center self-start px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                  project.status
                )}`}
              >
                {getStatusLabel(project.status)}
              </span>
            </div>
            <p className="text-lg text-secondary-600 leading-relaxed">
              {project.summary}
            </p>
          </div>

          {/* Problem Statement */}
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-accent-600 flex-shrink-0" aria-hidden="true" />
              <h2 className="text-xl font-semibold text-secondary-900">The Problem</h2>
            </div>
            <p className="text-secondary-700 leading-relaxed pl-7">
              {project.problem}
            </p>
          </section>

          {/* Impact Goal */}
          <section className="mb-10">
            <div className="bg-primary-50 border border-primary-100 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-primary-900 mb-2">Impact Goal</h2>
              <p className="text-primary-700 leading-relaxed">{project.impactGoal}</p>
            </div>
          </section>

          {/* Features */}
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Code className="h-5 w-5 text-primary-600 flex-shrink-0" aria-hidden="true" />
              <h2 className="text-xl font-semibold text-secondary-900">Key Features</h2>
            </div>
            <ul className="space-y-3 pl-7" role="list">
              {project.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle
                    className="h-5 w-5 text-success-600 flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span className="text-secondary-700">{feature}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Tech Stack */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4">Technology Stack</h2>
            <div className="flex flex-wrap gap-2">
              {project.techStack.map((tech) => (
                <span
                  key={tech}
                  className="bg-secondary-100 text-secondary-800 text-sm font-medium px-3 py-1.5 rounded-lg border border-secondary-200"
                >
                  {tech}
                </span>
              ))}
            </div>
          </section>

          {/* Links */}
          {(project.demoUrl || project.repoUrl) && (
            <section className="mb-10">
              <h2 className="text-xl font-semibold text-secondary-900 mb-4">Links</h2>
              <div className="flex flex-wrap gap-3">
                {project.demoUrl && (
                  <a
                    href={project.demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn bg-primary-600 text-white hover:bg-primary-500 inline-flex items-center no-underline hover:no-underline"
                  >
                    Live Demo
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                )}
                {project.repoUrl && (
                  <a
                    href={project.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn border border-secondary-300 text-secondary-700 hover:bg-secondary-50 inline-flex items-center no-underline hover:no-underline"
                  >
                    Source Code
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Fund this project CTA */}
          <section className="mb-16 bg-secondary-950 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Fund This Work</h2>
            <p className="text-secondary-300 mb-6 max-w-lg mx-auto">
              Your donation keeps {project.title} moving forward — covering infrastructure,
              development time, and the resources our learners depend on.
            </p>
            <Link
              to="/donate"
              className="btn btn-lg bg-primary-600 text-white hover:bg-primary-500 inline-flex items-center shadow-md no-underline hover:no-underline"
            >
              <Heart className="mr-2 h-5 w-5" />
              Support This Project
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </section>

          {/* Other projects */}
          {otherProjects.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-secondary-900 mb-6">
                More Projects
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherProjects.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/projects/${p.slug}`}
                    className="block bg-white rounded-xl border border-secondary-200 p-6 hover:shadow-md transition-shadow no-underline hover:no-underline"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(
                          p.status
                        )}`}
                      >
                        {getStatusLabel(p.status)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                      {p.title}
                    </h3>
                    <p className="text-sm text-secondary-600 line-clamp-3">
                      {p.summary}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <PublicFooter />
      <BackToTop />
    </div>
  );
};

export default ProjectDetail;
